import { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * NeuralBackground
 * A living canvas of neurons drifting and firing along synapses.
 * Mounted absolute / fixed by the parent — fills its closest positioned container.
 */
export default function NeuralBackground({ className = '', density = 1 }) {
  const canvasRef = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const palette = theme === 'light'
      ? {
        synapse: '90, 35, 55',           // muted maroon-grey lines
        synapseAlpha: 0.18,
        node: '31, 91, 107',             // deeper teal
        nodeBurst: '92, 26, 43',         // brand maroon
        glowAlpha: 0.55,
        pulseAlpha: 0.7,
      }
      : {
        synapse: '59, 152, 170',         // teal lines
        synapseAlpha: 0.14,
        node: '59, 152, 170',
        nodeBurst: '125, 36, 59',
        glowAlpha: 0.85,
        pulseAlpha: 0.95,
      };

    let neurons = [];
    let signals = [];
    let width = 0, height = 0, dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    let rafId = 0;
    let paused = false;
    let mouse = { x: -9999, y: -9999, active: false };

    function init() {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      // Density-aware count — guaranteed positive integer
      const target = Math.floor((width * height) / 16000 * density);
      const count = Math.max(40, Math.min(160, target || 40));

      neurons = [];
      for (let i = 0; i < count; i++) {
        neurons.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.18,
          vy: (Math.random() - 0.5) * 0.18,
          r: 0.9 + Math.random() * 1.3,
          phase: Math.random() * Math.PI * 2,
          chargeUntil: 0,
          burst: false,
          conn: [],
        });
      }

      // Connect each neuron to its 3–4 nearest neighbours
      neurons.forEach((n, i) => {
        const k = 3 + (Math.random() < 0.4 ? 1 : 0);
        const others = neurons
          .map((m, j) => ({ j, d: (n.x - m.x) * (n.x - m.x) + (n.y - m.y) * (n.y - m.y) }))
          .filter((o) => o.j !== i)
          .sort((a, b) => a.d - b.d)
          .slice(0, k);
        n.conn = others.map((o) => o.j);
      });

      signals = [];
    }

    function fire(idx, now, burst = false) {
      const n = neurons[idx];
      if (!n) return;
      n.chargeUntil = now + (burst ? 1100 : 700);
      n.burst = burst;
      const fanout = n.conn.length;
      n.conn.forEach((j, k) => {
        if (Math.random() < (burst ? 0.85 : 0.6)) {
          signals.push({
            fromIdx: idx,
            toIdx: j,
            progress: 0,
            speed: 0.010 + Math.random() * 0.018,
            burst,
            depth: 0,
            wobble: (k - fanout / 2) * 0.6 + (Math.random() - 0.5),
          });
        }
      });
    }

    function tick(now) {
      if (paused) { rafId = requestAnimationFrame(tick); return; }
      ctx.clearRect(0, 0, width, height);

      // Spontaneous ignition every few frames
      if (Math.random() < 0.07) {
        const idx = (Math.random() * neurons.length) | 0;
        fire(idx, now, Math.random() < 0.12);
      }

      // Move neurons
      const slowT = now * 0.0006;
      for (const n of neurons) {
        n.x += n.vx + Math.sin(slowT + n.phase) * 0.08;
        n.y += n.vy + Math.cos(slowT * 0.7 + n.phase) * 0.08;
        if (n.x < -10) n.x = width + 10;
        else if (n.x > width + 10) n.x = -10;
        if (n.y < -10) n.y = height + 10;
        else if (n.y > height + 10) n.y = -10;

        // Mouse attraction (subtle)
        if (mouse.active) {
          const dx = mouse.x - n.x;
          const dy = mouse.y - n.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 22000) {
            const f = (1 - d2 / 22000) * 0.05;
            n.vx += dx * 0.00004 * f;
            n.vy += dy * 0.00004 * f;
          }
        }
        // Friction so it doesn't run away
        n.vx *= 0.985;
        n.vy *= 0.985;
        if (Math.abs(n.vx) < 0.04) n.vx += (Math.random() - 0.5) * 0.02;
        if (Math.abs(n.vy) < 0.04) n.vy += (Math.random() - 0.5) * 0.02;
      }

      // Draw synapses
      ctx.lineWidth = 0.6;
      for (let i = 0; i < neurons.length; i++) {
        const n = neurons[i];
        for (const j of n.conn) {
          if (j <= i) continue; // each pair once
          const m = neurons[j];
          const dx = n.x - m.x, dy = n.y - m.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 190) {
            const a = (1 - d / 190) * palette.synapseAlpha;
            ctx.strokeStyle = `rgba(${palette.synapse}, ${a})`;
            ctx.beginPath();
            ctx.moveTo(n.x, n.y);
            ctx.lineTo(m.x, m.y);
            ctx.stroke();
          }
        }
      }

      // Draw signals (pulses with comet trail)
      signals = signals.filter((s) => {
        s.progress += s.speed;
        const from = neurons[s.fromIdx];
        const to = neurons[s.toIdx];
        if (!from || !to) return false;

        // Comet trail
        const trail = 7;
        const colorBase = s.burst ? palette.nodeBurst : palette.node;
        for (let i = 0; i < trail; i++) {
          const p = Math.max(0, s.progress - i * 0.035);
          if (p > 1) continue;
          const x = from.x + (to.x - from.x) * p;
          const y = from.y + (to.y - from.y) * p;
          const a = ((1 - i / trail) * palette.pulseAlpha) * Math.min(1, 1.5 - s.progress);
          ctx.fillStyle = `rgba(${colorBase}, ${a})`;
          ctx.beginPath();
          ctx.arc(x, y, Math.max(0.4, 1.6 - i * 0.18), 0, Math.PI * 2);
          ctx.fill();
        }

        if (s.progress >= 1) {
          // Arrived — flash receiver and possibly propagate
          to.chargeUntil = Math.max(to.chargeUntil, now + (s.burst ? 800 : 450));
          to.burst = s.burst || to.burst;
          if (s.depth < (s.burst ? 3 : 2)) {
            for (const k of to.conn) {
              if (k === s.fromIdx) continue;
              if (Math.random() < (s.burst ? 0.5 : 0.32)) {
                signals.push({
                  fromIdx: s.toIdx,
                  toIdx: k,
                  progress: 0,
                  speed: 0.010 + Math.random() * 0.02,
                  burst: s.burst && Math.random() < 0.6,
                  depth: s.depth + 1,
                });
              }
            }
          }
          return false;
        }
        return true;
      });
      if (signals.length > 220) signals.splice(220);

      // Draw neurons (glow + core)
      for (const n of neurons) {
        const charged = now < n.chargeUntil;
        const breath = 0.5 + 0.5 * Math.sin(now * 0.0011 + n.phase);
        const r = n.r + (charged ? 2.2 : 0) + breath * 0.6;
        const colorBase = charged && n.burst ? palette.nodeBurst : palette.node;
        const a = charged ? palette.glowAlpha : 0.32 + breath * 0.22;

        // Glow halo
        const haloR = r * 5;
        const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, haloR);
        g.addColorStop(0, `rgba(${colorBase}, ${a * 0.65})`);
        g.addColorStop(1, `rgba(${colorBase}, 0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(n.x, n.y, haloR, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.fillStyle = `rgba(${colorBase}, ${charged ? 1 : 0.85})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fill();

        // Reset burst flag once charge expires
        if (!charged && n.burst) n.burst = false;
      }

      rafId = requestAnimationFrame(tick);
    }

    function onResize() { init(); }
    function onMouseMove(e) {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = mouse.x >= 0 && mouse.y >= 0 && mouse.x <= width && mouse.y <= height;
    }
    function onMouseLeave() { mouse.active = false; mouse.x = -9999; mouse.y = -9999; }
    function onVisibility() { paused = document.hidden; }

    init();
    rafId = requestAnimationFrame(tick);
    window.addEventListener('resize', onResize);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseout', onMouseLeave);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseout', onMouseLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [theme, density]);

  return <canvas ref={canvasRef} className={`pointer-events-none ${className}`} />;
}
